import React, { useState, useEffect, useMemo } from 'react';
import { getAuditLogs, cleanupOldAuditLogs } from '../../api/auditLog';
import { getAllCompanies } from '../../api/companies';
import { useAuth } from '../../contexts/AuthContext';
import { useBetaData } from '../../contexts/BetaDataContext';
import useCompanyUsers from '../../hooks/useCompanyUsers';
import { useToast } from '../common/Toast';
import { useConfirm } from '../common/ConfirmDialog';
import CustomSelect from '../common/CustomSelect';
import './AuditLogViewer.scss';

// 動作類型（對齊 audit_logs.valid_action 白名單,詞彙同 WorkItemDetailDrawer 的 ACTION_LABELS）
const actionLabels = {
  create: '建立',
  update: '更新',
  delete: '刪除',
  assign: '指派',
  unassign: '取消指派',
  assignment_change: '變更指派',
  status_change: '變更狀態',
  archive: '封存',
  restore: '還原',
  move: '移動',
  batch_create: '批次建立',
  batch_update: '批次更新',
  batch_delete: '批次刪除',
};

// 資源類型
const resourceTypeLabels = {
  project: '專案',
  ticket: 'Ticket',
  item: 'ToDo',
  phase: '階段',
  ticket_comment: 'Ticket 留言',
  ticket_attachment: 'Ticket 附件',
  deliverable: '交付項目',
  deliverable_item: '工作項目(舊)', // 改名前的舊 resource_type(~2026-04),現行為 work_item
  // 新版工作項目家族
  work_item: '工作項目',
  work_item_group: '工作項目群組',
  work_item_comment: '工作項目留言',
  work_item_attachment: '工作項目附件',
  work_item_delivery_log: '工作項目交付紀錄',
};

// 角色
const roleLabels = {
  super_admin: '系統管理員',
  company_admin: '公司管理員',
  department_admin: '部門管理員',
  member: '成員',
};

// 動作顏色
const actionColors = {
  create: '#28a745',
  update: '#17a2b8',
  delete: '#dc3545',
  assign: '#6f42c1',
  unassign: '#fd7e14',
  assignment_change: '#7048e8',
  status_change: '#0ca678',
  archive: '#868e96',
  restore: '#20c997',
  move: '#f59f00',
  batch_create: '#28a745',
  batch_update: '#17a2b8',
  batch_delete: '#dc3545',
};

const AuditLogViewer = ({ isOpen, onClose }) => {
  const { profile, isSuperAdmin, isDepartmentAdmin, viewingCompanyId } = useAuth();
  const { projects } = useBetaData();
  const { users: companyUsers, usersMapByEmail } = useCompanyUsers();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // 篩選條件
  const [filters, setFilters] = useState({
    userId: '',
    companyId: '',
    resourceType: '',
    action: '',
    startDate: '',
    endDate: '',
    limit: 100,
    offset: 0
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // 專案名稱查詢表
  const projectsMap = useMemo(() => {
    const map = {};
    (projects || []).forEach(p => { map[p.id] = p.name; });
    return map;
  }, [projects]);

  // ─── CustomSelect 的選項 ──────────────────────
  const companyOptions = useMemo(() => [
    { value: '', label: '全部公司' },
    ...companies.map(c => ({ value: c.id, label: c.name })),
  ], [companies]);

  const userOptions = useMemo(() => [
    { value: '', label: '全部' },
    ...companyUsers.map(u => {
      const dept = u.department_name ? ` (${u.department_name})` : '';
      return { value: u.id, label: `${u.name || u.email}${dept}` };
    }),
  ], [companyUsers]);

  const resourceTypeOptions = useMemo(() => [
    { value: '', label: '全部' },
    ...Object.entries(resourceTypeLabels).map(([k, v]) => ({ value: k, label: v })),
  ], []);

  const actionOptions = useMemo(() => [
    { value: '', label: '全部' },
    ...Object.entries(actionLabels).map(([k, v]) => ({ value: k, label: v })),
  ], []);

  // ─── 初始化與載入 ──────────────────────
  useEffect(() => {
    if (isOpen && profile && (isSuperAdmin() || isDepartmentAdmin())) {
      if (!isInitialized) {
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(today.getMonth() - 1);
        const endOfToday = new Date(today);
        endOfToday.setHours(23, 59, 59, 999);

        setFilters(prev => ({
          ...prev,
          startDate: oneMonthAgo.toISOString().split('T')[0],
          endDate: endOfToday.toISOString()
        }));
        setIsInitialized(true);
        return;
      }
      loadCompanies();
    }
    if (!isOpen) {
      setIsInitialized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profile, isInitialized]);

  useEffect(() => {
    if (isOpen && isInitialized) {
      loadLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen, isInitialized,
    filters.userId, filters.companyId, filters.resourceType,
    filters.action, filters.startDate, filters.endDate,
    filters.limit, filters.offset
  ]);

  useEffect(() => {
    const isSuperAdminRole = profile?.role === 'super_admin';
    if (isOpen && isSuperAdminRole && !isInitialized) {
      setFilters(prev => ({ ...prev, companyId: viewingCompanyId || '' }));
    }
  }, [isOpen, viewingCompanyId, profile?.role, isInitialized]);

  const loadCompanies = async () => {
    if (isSuperAdmin()) {
      const { data, error } = await getAllCompanies();
      if (!error && data) setCompanies(data);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const { data, error, count } = await getAuditLogs({ ...filters });
      if (error) {
        toast.error('載入操作記錄失敗：' + error.message);
        return;
      }
      setLogs(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      toast.error('載入操作記錄異常：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── 篩選 handler ──────────────────────
  const updateFilter = (name, value) => {
    let finalValue = value;
    if (name === 'endDate' && value) {
      const d = new Date(value);
      d.setHours(23, 59, 59, 999);
      finalValue = d.toISOString();
    }
    setFilters(prev => ({ ...prev, [name]: finalValue, offset: 0 }));
    setCurrentPage(1);
  };

  const handleDateChange = (e) => {
    updateFilter(e.target.name, e.target.value);
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, offset: (newPage - 1) * itemsPerPage }));
    setCurrentPage(newPage);
  };

  const toggleExpand = (logId) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  // 清理舊記錄
  const handleCleanupOldLogs = async () => {
    const confirmed = await confirm({
      title: '清理舊記錄',
      message: '確定要刪除超過一年（365天）的操作記錄嗎？此操作無法復原！',
      confirmText: '確定刪除',
      cancelText: '取消',
      type: 'danger'
    });
    if (!confirmed) return;

    setIsCleaningUp(true);
    try {
      const { data, error } = await cleanupOldAuditLogs(365);
      if (error) { toast.error('清理失敗：' + error.message); return; }
      if (data?.success) {
        toast.success(`已清理 ${data.deleted_count} 筆舊記錄`);
        loadLogs();
      }
    } catch (error) {
      toast.error('清理異常：' + error.message);
    } finally {
      setIsCleaningUp(false);
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // ─── 從 log 中取得專案名稱（ticket / phase / work_item 用） ──────────────────────
  const getProjectName = (log) => {
    if (!['ticket', 'phase', 'work_item'].includes(log.resource_type)) return null;
    const projectId = log.changes?.after?.project_id || log.changes?.before?.project_id;
    if (!projectId) return null;
    return projectsMap[projectId] || null;
  };

  // ─── 渲染變更內容 ──────────────────────
  const renderChanges = (changes) => {
    if (!changes) return <div className="no-changes">無變更記錄</div>;
    const { before, after } = changes;

    if (!before && after) {
      return (
        <div className="changes-content">
          <div className="change-section">
            <div className="change-label">新建立的資料：</div>
            <pre className="change-value">{JSON.stringify(after, null, 2)}</pre>
          </div>
        </div>
      );
    }

    if (before && !after) {
      return (
        <div className="changes-content">
          <div className="change-section">
            <div className="change-label">已刪除的資料：</div>
            <pre className="change-value deleted">{JSON.stringify(before, null, 2)}</pre>
          </div>
        </div>
      );
    }

    if (before && after) {
      const changedFields = [];
      const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
      allKeys.forEach(key => {
        if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
          changedFields.push({ field: key, before: before[key], after: after[key] });
        }
      });

      if (changedFields.length === 0) {
        return <div className="no-changes">無實質變更</div>;
      }

      return (
        <div className="changes-content">
          <table className="changes-table">
            <thead>
              <tr>
                <th>欄位</th>
                <th>變更前</th>
                <th>變更後</th>
              </tr>
            </thead>
            <tbody>
              {changedFields.map(({ field, before: bv, after: av }) => (
                <tr key={field}>
                  <td className="field-name">{field}</td>
                  <td className="before-value">{JSON.stringify(bv)}</td>
                  <td className="after-value">{JSON.stringify(av)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <div className="no-changes">無變更記錄</div>;
  };

  if (!isOpen) return null;
  if (!profile || (!isSuperAdmin() && !isDepartmentAdmin())) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="audit-log-viewer" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>操作記錄</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {/* 工具列 */}
          <div className="toolbar">
            <div className="toolbar-left">
              <span className="records-count">共 <strong>{totalCount}</strong> 筆記錄</span>
            </div>
            {isSuperAdmin() && (
              <div className="toolbar-right">
                <button
                  className="btn-cleanup"
                  onClick={handleCleanupOldLogs}
                  disabled={isCleaningUp}
                >
                  {isCleaningUp ? '清理中...' : '清理舊記錄'}
                </button>
              </div>
            )}
          </div>

          {/* 篩選區 */}
          <div className="filters-section">
            {isSuperAdmin() && (
              <div className="filter-group">
                <label>公司</label>
                <CustomSelect
                  value={filters.companyId}
                  onChange={(v) => updateFilter('companyId', v)}
                  options={companyOptions}
                  placeholder="全部公司"
                />
              </div>
            )}

            <div className="filter-group">
              <label>使用者</label>
              <CustomSelect
                value={filters.userId}
                onChange={(v) => updateFilter('userId', v)}
                options={userOptions}
                placeholder="全部"
                searchable
              />
            </div>

            <div className="filter-group">
              <label>資源類型</label>
              <CustomSelect
                value={filters.resourceType}
                onChange={(v) => updateFilter('resourceType', v)}
                options={resourceTypeOptions}
                placeholder="全部"
              />
            </div>

            <div className="filter-group">
              <label>操作類型</label>
              <CustomSelect
                value={filters.action}
                onChange={(v) => updateFilter('action', v)}
                options={actionOptions}
                placeholder="全部"
              />
            </div>

            <div className="filter-group">
              <label>開始日期</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleDateChange}
              />
            </div>

            <div className="filter-group">
              <label>結束日期</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate ? filters.endDate.split('T')[0] : ''}
                onChange={handleDateChange}
              />
            </div>
          </div>

          {/* 記錄列表 */}
          <div className="logs-section">
            {loading ? (
              <div className="loading">載入中...</div>
            ) : logs.length === 0 ? (
              <div className="empty-state">
                <p>沒有符合條件的記錄</p>
              </div>
            ) : (
              <>
                <div className="logs-list">
                  {logs.map(log => {
                    const operatorRole = log.metadata?.operator_role;
                    const roleLabel = roleLabels[operatorRole] || operatorRole;
                    const operator = companyUsers.find(u => u.id === log.user_id);
                    const operatorMainUser = operator?.email ? usersMapByEmail[operator.email.toLowerCase()] : null;
                    const operatorDeptName = operatorMainUser?.department_name || '';
                    const projectName = getProjectName(log);

                    return (
                      <div key={log.id} className="log-item">
                        <div className="log-header" onClick={() => toggleExpand(log.id)}>
                          <div className="log-main-info">
                            <span
                              className="action-badge"
                              style={{ backgroundColor: actionColors[log.action] || '#6c757d' }}
                            >
                              {actionLabels[log.action] || log.action}
                            </span>
                            <span className="resource-type">
                              {resourceTypeLabels[log.resource_type] || log.resource_type}
                            </span>
                            {projectName && (
                              <span className="project-badge">{projectName}</span>
                            )}
                            <span className="resource-name">{log.resource_name || log.resource_id}</span>
                          </div>

                          <div className="log-meta-info">
                            <span className="user-name">
                              {log.user_name || log.user_email}
                              {isSuperAdmin() && (roleLabel || operatorDeptName) && (
                                <span className="user-role">
                                  （{roleLabel}{operatorDeptName ? ` / ${operatorDeptName}` : ''}）
                                </span>
                              )}
                            </span>
                            <span className="time">
                              {new Date(log.created_at).toLocaleString('zh-TW', {
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {log.changes && (
                              <span className="expand-icon">
                                {expandedLogId === log.id ? '▼' : '▶'}
                              </span>
                            )}
                          </div>
                        </div>

                        {expandedLogId === log.id && log.changes && (
                          <div className="log-details">
                            {renderChanges(log.changes)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ← 上一頁
                    </button>
                    <span className="page-info">
                      第 {currentPage} / {totalPages} 頁
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      下一頁 →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogViewer;
